import {
  fetchCachedAuthUser,
  readLastVerifiedOfflineUser,
} from "./utils/authCache";
import {
  clearAllAuthState,
  getUnifiedToken,
} from "./utils/unifiedAuth";
import {
  persistPreferredLanguage,
} from "./utils/avaaioLanguage";
import {
  usePreferences,
} from "./context/PreferencesContext";
import {
  getAvaaioUiCopy,
} from "./utils/avaaioUiCopy";
import System from "./pages/System";
import FunctionsPage from "./pages/education/FunctionsPage";
import FractionsPage from "./pages/education/FractionsPage";
import DivisionPage from "./pages/education/DivisionPage";
import MultiplicationPage from "./pages/education/MultiplicationPage";
import SubtractionPage from "./pages/education/SubtractionPage";
import AdditionPage from "./pages/education/AdditionPage";
import GradeLevelsPage from "./pages/education/GradeLevelsPage";
import FlashCardsPage from "./pages/education/FlashCardsPage";
import MathBoardPage from "./pages/education/MathBoardPage";
import TutorPage from "./pages/education/TutorPage";
import EduMath from "./pages/education/Math";
import EduHistory from "./pages/education/History";
import EduReading from "./pages/education/Reading";
import EduScience from "./pages/education/Science";
import Education from "./pages/Education";
import CheckersGame from "./pages/games/CheckersGame";
import ChessGame from "./pages/games/ChessGame";
import Chess3DBoard from "./pages/games/Chess3DBoard";
import GamesHub from "./pages/GamesHub";
import Entertainment from "./pages/Entertainment";
import Info from "./pages/Info";
import Support from "./pages/Support";
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ReactivateAccount from "./pages/ReactivateAccount";
import SetupProfile from "./pages/SetupProfile";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Setup from "./pages/Setup";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import AIDisclaimer from "./pages/AIDisclaimer";
import Credits from "./pages/Credits";
import Theme from "./pages/Theme";
import Profile from "./pages/Profile";
import Account from "./pages/Account";
import Settings from "./pages/Settings";
import Language from "./pages/Language";
import Subscribe from "./pages/Subscribe";
import SettingsPlanPanel from "./components/SettingsPlanPanel";
import ConnectionModePanel from "./components/ConnectionModePanel";
import PageBottomNav from "./components/PageBottomNav";
import AdminConsole from "./pages/AdminConsole";

const API_BASE = import.meta.env.VITE_API_BASE || "";

type UserState =
  | null
  | {
      role: string;
      status?: string;
      email?: string;
      preferred_language?: string;
      offline_entitlement?: string;
      subscription_status?: string;
    };


// __AVAAIO_SERVER_VERIFIED_SETUP_ROUTE_V9__
function ServerVerifiedSetupRoute() {
  const [routeState, setRouteState] = React.useState<{
    checking: boolean;
    allowed: boolean;
    redirect: string;
  }>({
    checking: true,
    allowed: false,
    redirect: "/login",
  });

  React.useEffect(() => {
    let cancelled = false;

    async function verifySetupAuthorization() {
      const token = getUnifiedToken();

      try {
        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              ...(token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {}),
            },
          },
        );

        if (!response.ok) {
          if (!cancelled) {
            setRouteState({
              checking: false,
              allowed: false,
              redirect: "/login",
            });
          }

          return;
        }

        const payload = await response.json();

        const serverUser =
          payload?.user ||
          payload?.profile ||
          payload?.me ||
          payload;

        const verificationValue =
          serverUser?.email_verified ??
          serverUser?.is_verified;

        const serverVerified =
          verificationValue === true ||
          verificationValue === 1 ||
          verificationValue === "1";

        if (!serverVerified) {
          try {
            clearAllAuthState();
          } catch {}

          if (!cancelled) {
            setRouteState({
              checking: false,
              allowed: false,
              redirect:
                "/login?verification=required",
            });
          }

          return;
        }

        if (!cancelled) {
          setRouteState({
            checking: false,
            allowed: true,
            redirect: "",
          });
        }
      } catch {
        if (!cancelled) {
          setRouteState({
            checking: false,
            allowed: false,
            redirect: "/login",
          });
        }
      }
    }

    verifySetupAuthorization();

    return () => {
      cancelled = true;
    };
  }, []);

  if (routeState.checking) {
    return (
      <div className="min-h-screen bg-[#02060d] text-white flex items-center justify-center">
        <div className="text-cyan-300 text-sm tracking-[0.22em] uppercase">
          Verifying secure account access…
        </div>
      </div>
    );
  }

  if (!routeState.allowed) {
    return (
      <Navigate
        to={routeState.redirect}
        replace
      />
    );
  }

  return <SetupProfile />;
}


function AdminAccessGate({
  onVerified,
}: {
  onVerified: (user: NonNullable<UserState>) => void;
}) {
  const [state, setState] = React.useState<
    "checking" | "allowed" | "offline" | "denied"
  >("checking");

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();

    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, 10000);

    const verifyAdmin = async () => {
      try {
        const token = getUnifiedToken();

        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            signal: controller.signal,
            headers: {
              Accept: "application/json",
              ...(token
                ? { Authorization: `Bearer ${token}` }
                : {}),
            },
          }
        );

        if (disposed) return;

        if (!response.ok) {
          setState("denied");
          return;
        }

        const payload = await response
          .json()
          .catch(() => null);

        const liveUser =
          payload?.user ?? payload;

        const role =
          String(liveUser?.role || "")
            .trim()
            .toLowerCase();

        const isAdmin = [
          "admin",
          "owner",
          "superadmin",
          "super_admin",
        ].includes(role);

        if (!isAdmin) {
          setState("denied");
          return;
        }

        const verifiedUser: NonNullable<UserState> = {
          role,
          status:
            typeof liveUser?.status === "string"
              ? liveUser.status
              : undefined,
          email:
            typeof liveUser?.email === "string"
              ? liveUser.email
              : undefined,
          preferred_language:
            typeof liveUser?.preferred_language === "string"
              ? liveUser.preferred_language
              : undefined,
          subscription_status:
            typeof liveUser?.subscription_status === "string"
              ? liveUser.subscription_status
              : undefined,
        };

        onVerified(verifiedUser);

        try {
          localStorage.setItem(
            "ava_user_role",
            role
          );
          sessionStorage.setItem(
            "ava_user_role",
            role
          );

          localStorage.setItem(
            "ava_chat_plan",
            "admin"
          );
          sessionStorage.setItem(
            "ava_chat_plan",
            "admin"
          );

          window.dispatchEvent(
            new CustomEvent("ava-plan-updated", {
              detail: {
                role,
                plan: "admin",
                subscription_status:
                  liveUser?.subscription_status,
              },
            })
          );
        } catch {}

        setState("allowed");
      } catch {
        if (!disposed) {
          window.alert(
            "Admin Console is unavailable while offline. " +
            "Connect to the internet to manage AVAAIO. " +
            "Your Local Mode session remains active."
          );

          setState("offline");
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    void verifyAdmin();

    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [onVerified]);

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#02060d] text-white flex items-center justify-center">
        <div className="text-cyan-300 text-sm tracking-[0.2em] uppercase">
          Verifying Admin access…
        </div>
      </div>
    );
  }

  if (state === "allowed") {
    return <AdminConsole />;
  }

  if (state === "offline") {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
}

export default function App() {
  const { preferences } = usePreferences();
  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";

  const loggedOut =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("logged_out") === "1";

  const logoutLatch =
    typeof window !== "undefined" &&
    (() => {
      try {
        return sessionStorage.getItem("ava_logged_out") === "1";
      } catch {
        return false;
      }
    })();

  const logoutInProgress = loggedOut || logoutLatch;
  const isLoginRoute = pathname.startsWith("/login");
  const hasToken =
      typeof window !== "undefined" &&
      (() => {
        try {
          return !!(
            localStorage.getItem("token") ||
            localStorage.getItem("access_token") ||
            sessionStorage.getItem("token")
          );
        } catch {
          return false;
        }
      })();

    const shouldBypassBootstrap =
      isLoginRoute || (logoutInProgress && !hasToken);

  const [user, setUser] = useState<UserState>(null);
  const [loading, setLoading] = useState(!shouldBypassBootstrap);
  const [languageReady, setLanguageReady] =
    useState(shouldBypassBootstrap);

  function resolveAuthToken(): string {
      return getUnifiedToken();
    }

  useEffect(() => {
    let cancelled = false;

    if (shouldBypassBootstrap) {
      setUser(null);
      setLanguageReady(true);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLanguageReady(false);
    setLoading(true);

    const offlineUser =
      typeof navigator !== "undefined" &&
      navigator.onLine === false
        ? readLastVerifiedOfflineUser()
        : null;

    if (offlineUser) {
      persistPreferredLanguage(
        offlineUser.preferred_language || "en"
      );

      const offlineEntitlement =
        String(
          offlineUser.offline_entitlement ||
          "free"
        )
          .trim()
          .toLowerCase();

      const offlineLocalPlan =
        offlineEntitlement === "admin" ||
        offlineEntitlement === "pro"
          ? "pro"
          : "free";

      try {
        // Local feature plan only. This does not grant Admin authority.
        localStorage.setItem(
          "ava_chat_plan",
          offlineLocalPlan
        );
        sessionStorage.setItem(
          "ava_chat_plan",
          offlineLocalPlan
        );

        localStorage.setItem(
          "subscription_status",
          offlineLocalPlan === "pro"
            ? "active"
            : "free"
        );
        sessionStorage.setItem(
          "subscription_status",
          offlineLocalPlan === "pro"
            ? "active"
            : "free"
        );

        // Administrative authority always remains restricted offline.
        localStorage.setItem(
          "ava_user_role",
          "user"
        );
        sessionStorage.setItem(
          "ava_user_role",
          "user"
        );
      } catch {}

      setUser({
        role: "user",
        status: offlineUser.status || "active",
        email: offlineUser.email,
        preferred_language:
          offlineUser.preferred_language,

        // Feature entitlement only.
        offline_entitlement:
          offlineUser.offline_entitlement,
        subscription_status:
          offlineUser.subscription_status,
      });

      setLanguageReady(true);
      setLoading(false);

      return () => {
        cancelled = true;
      };
    }

    const token = resolveAuthToken();

      fetchCachedAuthUser(`${API_BASE}/api/v1/auth/me`, {
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
      .then((authUser) => {
        if (cancelled) return;
        if (authUser) {
          persistPreferredLanguage(
            authUser?.preferred_language || "en"
          );

          setUser({
            role: typeof authUser.role === "string" ? authUser.role : "user",
            status:
              typeof authUser.status === "string" ? authUser.status : undefined,
            email:
              typeof authUser.email === "string"
                ? authUser.email
                : undefined,
            preferred_language:
              typeof authUser.preferred_language ===
              "string"
                ? authUser.preferred_language
                : undefined,
          });
        } else {
          setUser(null);
        }
      })
      .catch((err) => {
        console.warn("[App] auth/me failed:", err);
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (cancelled) return;

        window.setTimeout(() => {
          if (cancelled) return;

          setLanguageReady(true);
          setLoading(false);
        }, 0);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldBypassBootstrap]);

  // =====================================================
  // AVAAIO ONLINE ROLE RECOVERY
  //
  // Offline Local Mode intentionally restricts authority to
  // role:user. When internet access returns, verify server truth
  // immediately and restore the authenticated live account role.
  // =====================================================
  useEffect(() => {
    let disposed = false;
    let requestInFlight = false;

    const restoreLiveAccountRole = async () => {
      if (
        disposed ||
        requestInFlight ||
        typeof navigator === "undefined" ||
        navigator.onLine === false
      ) {
        return;
      }

      requestInFlight = true;

      try {
        const token = resolveAuthToken();

        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
              ...(token
                ? { Authorization: `Bearer ${token}` }
                : {}),
            },
          }
        );

        if (!response.ok) {
          // The existing heartbeat remains responsible for
          // authoritative 401/403/404 revocation handling.
          return;
        }

        const payload = await response
          .json()
          .catch(() => null);

        const liveUser =
          payload?.user ?? payload;

        if (
          disposed ||
          !liveUser ||
          typeof liveUser !== "object"
        ) {
          return;
        }

        const liveRole =
          typeof liveUser.role === "string"
            ? liveUser.role
            : "user";

        setUser({
          role: liveRole,
          status:
            typeof liveUser.status === "string"
              ? liveUser.status
              : undefined,
          email:
            typeof liveUser.email === "string"
              ? liveUser.email
              : undefined,
          preferred_language:
            typeof liveUser.preferred_language ===
            "string"
              ? liveUser.preferred_language
              : undefined,
          subscription_status:
            typeof liveUser.subscription_status ===
            "string"
              ? liveUser.subscription_status
              : undefined,
        });

        try {
          localStorage.setItem(
            "ava_user_role",
            liveRole
          );
          sessionStorage.setItem(
            "ava_user_role",
            liveRole
          );

          const normalizedRole =
            String(liveRole)
              .trim()
              .toLowerCase();

          const normalizedSubscription =
            String(
              liveUser.subscription_status || ""
            )
              .trim()
              .toLowerCase();

          const isAdmin = [
            "admin",
            "owner",
            "superadmin",
            "super_admin",
          ].includes(normalizedRole);

          const isPro =
            isAdmin ||
            normalizedRole === "vip" ||
            normalizedRole === "pro" ||
            normalizedSubscription === "active";

          const livePlan =
            isAdmin
              ? "admin"
              : isPro
                ? "pro"
                : "free";

          localStorage.setItem(
            "ava_chat_plan",
            livePlan
          );
          sessionStorage.setItem(
            "ava_chat_plan",
            livePlan
          );

          localStorage.setItem(
            "subscription_status",
            normalizedSubscription ||
              (isPro ? "active" : "free")
          );
          sessionStorage.setItem(
            "subscription_status",
            normalizedSubscription ||
              (isPro ? "active" : "free")
          );

          window.dispatchEvent(
            new CustomEvent("ava-plan-updated", {
              detail: {
                role: liveRole,
                plan: livePlan,
                subscription_status:
                  liveUser.subscription_status,
              },
            })
          );
        } catch {}

        console.log(
          "[AVAAIO Online Recovery] Live role restored:",
          liveRole
        );
      } catch (error) {
        console.warn(
          "[AVAAIO Online Recovery] Server still unreachable:",
          error
        );
      } finally {
        requestInFlight = false;
      }
    };

    const handleOnline = () => {
      void restoreLiveAccountRole();
    };

    window.addEventListener(
      "online",
      handleOnline
    );

    if (navigator.onLine !== false) {
      void restoreLiveAccountRole();
    }

    return () => {
      disposed = true;
      window.removeEventListener(
        "online",
        handleOnline
      );
    };
  }, [shouldBypassBootstrap]);

  // =====================================================
  // AVAAIO LIVE ACCOUNT ACCESS HEARTBEAT
  //
  // When an administrator bans, denies, or deletes a user,
  // /auth/me reads the current database record and returns
  // 401, 403, or 404. Clear the local session and return
  // the affected user to login on every open device.
  // =====================================================
  useEffect(() => {
    let disposed = false;
    let requestInFlight = false;

    const publicRoutePrefixes = [
      "/login",
      "/register",
      "/verify-email",
      "/forgot-password",
      "/reset-password",
      "/reactivate-account",
    ];

    const isPublicRoute = () => {
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname
          : "/";

      return publicRoutePrefixes.some((prefix) =>
        currentPath.startsWith(prefix)
      );
    };

    const forceAccountLogout = async (status: number) => {
      if (disposed) return;

      disposed = true;

      try {
        sessionStorage.setItem("ava_logged_out", "1");
      } catch {}

      try {
        clearAllAuthState();
      } catch {}

      try {
        await fetch(`${API_BASE}/api/v1/auth/logout`, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
        });
      } catch {
        // The server may already have invalidated the account.
      }

      const reason =
        status === 403
          ? "access_revoked"
          : status === 404
            ? "account_removed"
            : "session_expired";

      window.location.replace(
        `/login?logged_out=1&reason=${reason}`
      );
    };

    const verifyLiveAccountAccess = async () => {
      if (
        disposed ||
        requestInFlight ||
        isPublicRoute() ||
        (
          typeof navigator !== "undefined" &&
          navigator.onLine === false
        )
      ) {
        return;
      }

      requestInFlight = true;

      try {
        const token = getUnifiedToken();

        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              ...(token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {}),
            },
          },
        );

        if (
          response.status === 401 ||
          response.status === 403 ||
          response.status === 404
        ) {
          await forceAccountLogout(response.status);
        }
      } catch {
        // Never log out merely because the network or API
        // is temporarily unavailable.
      } finally {
        requestInFlight = false;
      }
    };

    const handleFocus = () => {
      void verifyLiveAccountAccess();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void verifyLiveAccountAccess();
      }
    };

    void verifyLiveAccountAccess();

    const intervalId = window.setInterval(
      () => {
        void verifyLiveAccountAccess();
      },
      15000,
    );

    window.addEventListener("focus", handleFocus);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, []);

  if (loading || !languageReady) {
    return (
      <div className="min-h-screen bg-[#02060d] text-white flex items-center justify-center">
        <div className="text-cyan-300 text-sm tracking-[0.25em] uppercase">
          {interfaceCopy.dashboard.loadingAva}
        </div>
      </div>
    );
  }

  return (
      <>
        <Routes>
      <Route
        path="/"
        element={
          (
            typeof navigator !== "undefined" &&
            navigator.onLine === false &&
            readLastVerifiedOfflineUser()
          )
            ? <Navigate to="/dashboard" replace />
            : <Navigate to="/login" replace />
        }
      />
      <Route path="/login/*" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/setup" element={<Setup />} />
      <Route
        path="/setup-profile"
        element={<ServerVerifiedSetupRoute />}
      />
      <Route
        path="/profile-setup"
        element={<ServerVerifiedSetupRoute />}
      />
      <Route path="/verify-email" element={<VerifyEmail />} />

      <Route
        path="/dashboard"
        element={user ? <Dashboard /> : <Navigate to="/login" replace />}
      />

      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/ai-disclaimer" element={<AIDisclaimer />} />
      <Route path="/credits" element={<Credits />} />

      <Route
        path="/admin"
        element={
          [
            "admin",
            "owner",
            "superadmin",
            "super_admin",
          ].includes(
            String(user?.role || "")
              .trim()
              .toLowerCase()
          ) ? (
            <AdminConsole />
          ) : (
            <AdminAccessGate
              onVerified={setUser}
            />
          )
        }
      />

      
      <Route path="/info" element={<Info />} />
      <Route path="/support" element={<Support />} />
      <Route path="/entertainment" element={<Entertainment />} />
      <Route path="/games" element={<GamesHub />} />

      <Route path="/education" element={<Education />} />
      <Route path="/education/tutor" element={<TutorPage />} />
      <Route
        path="/education/decimals"
        element={<TutorPage initialTopic="Decimals" lockTopic />}
      />
      <Route
        path="/education/essay-writing"
        element={<TutorPage initialTopic="Essay Writing" lockTopic />}
      />
      <Route path="/education/mathboard" element={<MathBoardPage />} />
      <Route path="/education/math" element={<EduMath />} />
      <Route path="/education/history" element={<EduHistory />} />
      <Route path="/education/reading" element={<EduReading />} />
      <Route path="/education/science" element={<EduScience />} />
      <Route path="/education/flashcards" element={<FlashCardsPage />} />
      <Route path="/education/grades" element={<GradeLevelsPage />} />

      <Route path="/education/addition" element={<AdditionPage />} />
      <Route path="/education/subtraction" element={<SubtractionPage />} />
      <Route path="/education/multiplication" element={<MultiplicationPage />} />
      <Route path="/education/division" element={<DivisionPage />} />
      <Route path="/education/fractions" element={<FractionsPage />} />
      <Route path="/education/functions" element={<FunctionsPage />} />



    
      <Route path="/settings" element={<><SettingsPlanPanel /><ConnectionModePanel /><Settings /></>} />
      <Route path="/language" element={<Language />} />
      <Route path="/system" element={<System />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/account" element={<Account />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/theme" element={<Theme />} />

      
      <Route path="/games/chess" element={<ChessGame />} />
          <Route path="/games/chess-3d-preview" element={<Chess3DBoard />} />
      <Route path="/games/checkers" element={<CheckersGame />} />

      
<Route path="/forgot-password" element={<ForgotPassword />} />
<Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reactivate" element={<ReactivateAccount />} />

<Route path="*" element={<Navigate to="/login" replace />} />

        </Routes>
        <PageBottomNav
          authenticated={Boolean(user)}
        />
      </>
  );
}
